import type { ClassLessonAlarmOffset, ClassLessonAlarmSoundKind } from './classLessonAlarmSettings';

/**
 * 수업 알람 크로스탭 공유.
 * - 메인 탭이 계산한 오늘 울림 대상을 LS에 공유 → 프레젠 탭도 메인 탭이 멈춰도 스스로 울림.
 * - 알람끄기(오늘 해당 세션)는 LS로 모든 탭이 공유.
 * - 같은 울림(분 단위)은 한 탭만 소리를 재생 — 보이는 탭 우선 선점.
 */

export type ClassLessonAlarmSharedTarget = {
  time: string;
  offset: ClassLessonAlarmOffset;
  soundKind: ClassLessonAlarmSoundKind;
  channel: 'regular' | 'dedicated';
  gradeLabel: string;
  periodLabel: string;
};

type SharedSchedule = { dayIso: string; targets: ClassLessonAlarmSharedTarget[] };
type SharedDismissed = { dayIso: string; keys: string[] };
type SoundClaim = { key: string; tab: string; at: number };

const scheduleKey = (sid: string) => `cp.classLessonAlarm.schedule.v1:${sid}`;
const dismissedKey = (sid: string) => `cp.classLessonAlarm.dismissed.v1:${sid}`;
const claimKey = (sid: string) => `cp.classLessonAlarm.soundClaim.v1:${sid}`;

const TAB_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/** 숨겨진 탭은 이만큼 늦게 선점 시도 — 보이는 탭이 먼저 가져가도록 */
const HIDDEN_TAB_CLAIM_DELAY_MS = 700;
const CLAIM_SETTLE_MS = 80;

function readJson<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota 등 무시 */
  }
}

const sleep = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));

export function publishClassLessonAlarmSchedule(
  workspaceId: string,
  dayIso: string,
  targets: ClassLessonAlarmSharedTarget[]
): void {
  const sid = workspaceId.trim();
  if (!sid || typeof window === 'undefined') return;
  const next: SharedSchedule = { dayIso, targets };
  const nextRaw = JSON.stringify(next);
  try {
    if (window.localStorage.getItem(scheduleKey(sid)) === nextRaw) return;
  } catch {
    return;
  }
  writeJson(scheduleKey(sid), next);
}

export function readClassLessonAlarmSchedule(
  workspaceId: string,
  dayIso: string
): ClassLessonAlarmSharedTarget[] {
  const sid = workspaceId.trim();
  if (!sid || typeof window === 'undefined') return [];
  const s = readJson<SharedSchedule>(scheduleKey(sid));
  if (!s || s.dayIso !== dayIso || !Array.isArray(s.targets)) return [];
  return s.targets;
}

export function readClassLessonAlarmDismissed(workspaceId: string, dayIso: string): Set<string> {
  const sid = workspaceId.trim();
  if (!sid || typeof window === 'undefined') return new Set();
  const d = readJson<SharedDismissed>(dismissedKey(sid));
  if (!d || d.dayIso !== dayIso || !Array.isArray(d.keys)) return new Set();
  return new Set(d.keys);
}

export function addClassLessonAlarmDismissed(
  workspaceId: string,
  dayIso: string,
  alarmKey: string
): void {
  const sid = workspaceId.trim();
  if (!sid || typeof window === 'undefined') return;
  const keys = readClassLessonAlarmDismissed(sid, dayIso);
  if (keys.has(alarmKey)) return;
  keys.add(alarmKey);
  writeJson(dismissedKey(sid), { dayIso, keys: Array.from(keys) } satisfies SharedDismissed);
}

/**
 * 같은 울림(triggerKey)에 대해 소리를 재생할 탭 1개를 정한다.
 * 보이는 탭은 즉시, 숨겨진 탭은 잠시 뒤 시도 → 보이는 탭이 있으면 그 탭이 재생.
 */
export async function claimClassLessonAlarmSound(
  workspaceId: string,
  triggerKey: string
): Promise<boolean> {
  const sid = workspaceId.trim();
  if (!sid || typeof window === 'undefined') return true;
  if (document.visibilityState !== 'visible') await sleep(HIDDEN_TAB_CLAIM_DELAY_MS);
  const cur = readJson<SoundClaim>(claimKey(sid));
  if (cur && cur.key === triggerKey && cur.tab !== TAB_ID) return false;
  writeJson(claimKey(sid), { key: triggerKey, tab: TAB_ID, at: Date.now() } satisfies SoundClaim);
  await sleep(CLAIM_SETTLE_MS);
  const after = readJson<SoundClaim>(claimKey(sid));
  return !after || (after.key === triggerKey && after.tab === TAB_ID);
}

/** 재생 실패(자동재생 차단 등) 시 다른 탭이 가져가도록 선점 해제 */
export function releaseClassLessonAlarmSound(workspaceId: string, triggerKey: string): void {
  const sid = workspaceId.trim();
  if (!sid || typeof window === 'undefined') return;
  const cur = readJson<SoundClaim>(claimKey(sid));
  if (cur && cur.key === triggerKey && cur.tab === TAB_ID) {
    try {
      window.localStorage.removeItem(claimKey(sid));
    } catch {
      /* ignore */
    }
  }
}

export function isClassLessonAlarmSharedStorageKey(workspaceId: string, key: string | null): boolean {
  const sid = workspaceId.trim();
  return !!key && (key === scheduleKey(sid) || key === dismissedKey(sid));
}

/** 바탕화면 알림 — 모든 창이 최소화·가려져 있을 때 알람을 화면에 띄우고 클릭 시 탭으로 복귀 */
export function canShowClassLessonAlarmDesktopNotice(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function showClassLessonAlarmDesktopNotice(params: {
  title: string;
  body: string;
  tag: string;
  onClick?: () => void;
}): void {
  if (!canShowClassLessonAlarmDesktopNotice() || Notification.permission !== 'granted') return;
  try {
    const n = new Notification(params.title, {
      body: params.body,
      tag: params.tag,
      requireInteraction: true,
      icon: `${process.env.PUBLIC_URL || ''}/favicon.ico`,
    });
    n.onclick = () => {
      try {
        window.focus();
      } catch {
        /* ignore */
      }
      params.onClick?.();
      n.close();
    };
  } catch {
    /* 일부 환경(모바일 등)은 생성자 미지원 */
  }
}
